import {useMutation, useQueryClient} from '@tanstack/react-query'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {httpChangePhotographerStatus, HttpChangePhotographerStatusOptions} from '@features/admin/http/admin.http'
import {User} from '@features/user/types.ts'
import {captureException} from '@sentry/react'
import {errorHandler, raise} from '@utilities/helpers.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {z} from 'zod'
import {useAuthStore} from '@features/user/store/store'

const UserStatusShape = User.pick({
    status: true
})
export type UserStatusShape = z.infer<typeof UserStatusShape>

export const useChangeFreelancerStatus = ({
    photographerId,
    onSuccess
}: {
    photographerId: number
    onSuccess: () => void
}) => {
    const queryClient = useQueryClient()
    const adminAccessToken = useAuthStore(state => state.adminAccessToken) ?? raise('adminAccessToken is nullish')
    return useMutation<UserStatusShape, unknown, HttpChangePhotographerStatusOptions['payload']>({
        mutationFn: payload =>
            parseAxiosPromise({
                axiosPromise: httpChangePhotographerStatus({
                    payload,
                    urlParams: {photographerId, adminAccessToken}
                }),
                responseShape: UserStatusShape,
                onZodError: captureException
            }),
        onError: errorHandler,
        onSuccess: async data => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.ME]})

            queryClient.setQueryData(
                [QUERY_KEYS.ME],
                (previousData: User): User => ({
                    ...previousData,
                    status: data.status
                })
            )
            onSuccess()
        }
    })
}
